import { useQuery } from "@tanstack/react-query";
import { getSubmission, type Api } from "api-client";
import { Icon } from "icons";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "ui";

import { QuestionnaireSubmissionNavigation } from "src/components/QuesionnaireSubmissionNavigation";
import { QuestionnaireSubmissionContextProvider } from "src/questionnaire/QuestionnaireSubmissionContext";
import { QuestionnaireSubmissionFill } from "src/questionnaire/QuestionnaireSubmissionFill";

type ProfileLead = Api.ListProfileLeads.Output.CurrentPage;

interface ProfileLeadSubmissionDialogProps {
  lead: ProfileLead;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ProfileLeadSubmissionDialog(
  props: ProfileLeadSubmissionDialogProps,
) {
  const { t } = useTranslation();
  const [submissionIndex, setSubmissionIndex] = useState(0);
  const submission = props.lead.submissions[submissionIndex]!;

  const submissionQuery = useQuery({
    queryKey: ["questionnaire-submission", submission.id],
    queryFn: async () => {
      return await getSubmission({
        path: { submissionId: submission.id },
      }).then((response) => response.data.data);
    },
    enabled: props.open && submission.questionnaire !== null,
  });

  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange}>
      <DialogContent className="flex h-[90vh] max-h-[90vh] flex-col gap-0 p-0 sm:max-w-3xl">
        <DialogHeader className="shrink-0 border-b px-6 py-4 pr-12">
          <DialogTitle>{submission.name}</DialogTitle>
          <DialogDescription>
            {props.lead.email} ·{" "}
            {submission.questionnaire?.name ??
              t("profileContactFormDefaultTitle")}
          </DialogDescription>
        </DialogHeader>
        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4">
          {!submission.questionnaire ? (
            <div className="space-y-6">
              <dl className="grid gap-4 rounded-lg border p-4 sm:grid-cols-2">
                <div>
                  <dt className="text-xs font-medium text-muted-foreground">
                    {t("profileContactFormName")}
                  </dt>
                  <dd className="mt-1 text-sm">{submission.name}</dd>
                </div>
                <div>
                  <dt className="text-xs font-medium text-muted-foreground">
                    {t("profileContactFormEmail")}
                  </dt>
                  <dd className="mt-1 break-all text-sm">{props.lead.email}</dd>
                </div>
              </dl>
              <div className="flex items-center justify-between gap-3">
                <Button
                  type="button"
                  variant="outline"
                  disabled={submissionIndex === 0}
                  onClick={() => {
                    setSubmissionIndex((index) => index - 1);
                  }}
                >
                  {t("questionnaireSubmissionNavigation.previous")}
                </Button>
                <span className="text-sm text-muted-foreground">
                  {t("questionnaireSubmissionNavigation.position", {
                    current: submissionIndex + 1,
                    total: props.lead.submissions.length,
                  })}
                </span>
                <Button
                  type="button"
                  variant="outline"
                  disabled={
                    submissionIndex === props.lead.submissions.length - 1
                  }
                  onClick={() => {
                    setSubmissionIndex((index) => index + 1);
                  }}
                >
                  {t("questionnaireSubmissionNavigation.next")}
                </Button>
              </div>
            </div>
          ) : submissionQuery.isLoading ? (
            <div className="flex h-full items-center justify-center">
              <Icon
                name="tabler-loader-2"
                className="size-5 animate-spin text-muted-foreground"
              />
            </div>
          ) : submissionQuery.data ? (
            <>
              <QuestionnaireSubmissionNavigation
                submission={submissionQuery.data}
                showAthleteLink={false}
                standalone
                hasPrevious={submissionIndex > 0}
                hasNext={submissionIndex < props.lead.submissions.length - 1}
                position={{
                  current: submissionIndex + 1,
                  total: props.lead.submissions.length,
                }}
                onPrevious={() => {
                  setSubmissionIndex((index) => index - 1);
                }}
                onNext={() => {
                  setSubmissionIndex((index) => index + 1);
                }}
              />
              <QuestionnaireSubmissionContextProvider
                key={submissionQuery.data.id}
                questionnaireSubmission={submissionQuery.data}
              >
                <QuestionnaireSubmissionFill />
              </QuestionnaireSubmissionContextProvider>
            </>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}
